import { useEffect, type Dispatch, type SetStateAction } from 'react';
import L from 'leaflet';
import * as turf from '@turf/turf';
import type { GridParams } from 'contexts/GridContext';
import { isWithinSwitzerland } from 'services/isWithinSwitzerland';
import { MAX_ALLOWED_GRID_AREA, MIN_ANALYSIS_AREA } from 'constants/grid';
import { roundCoordinatesTo8Digits, wgs84ToLv95ForOneCoordinate } from '../map/coordinateTransforms';
import type { GridMapType } from '../map/mapConfig';
import {
  cancelActiveDrawing,
  clearActiveDrawButton,
  highlightActiveDrawButton,
  isDrawButtonActive,
  normalizeRectangle,
  triggerDrawing,
} from 'lib/leafletHelper.js';
import {
  getLayerMap,
  POLYGON_DRAW_HINT,
  RECTANGLE_DRAW_HINT,
  useStableHandler,
  type BorderType,
  type Lv95Coordinate,
  type SetGridParams,
  type ShapeType,
  type TurfPolygon,
} from './cadastralShared';
import { getCombinedAnalysisAreasCenter } from './cadastralGeometry';
import type { AddCenterMarker } from './AnalysisAreaCenterMarkerSync';

interface DrawCreatedEvent {
  layer: L.Polygon;
  layerType: string;
}

interface CadastralDrawingOptions {
  mapType: GridMapType;
  activeEditBorderType: BorderType | null;
  isLegalBorderParcelMode: boolean;
  setIsLegalBorderParcelMode: Dispatch<SetStateAction<boolean>>;
  gridParams: GridParams;
  analysisAreas: Lv95Coordinate[][];
  activeAnalysisAreaIndex: number;
  isDrawingRef: { current: boolean };
  pendingDrawRef: { current: { shapeType: ShapeType | null; borderType: BorderType | null } | null };
  currentTypeOfPolygonRef: { current: BorderType | null };
  currentShapeTypeRef: { current: ShapeType | null };
  activeEditBorderRef: { current: BorderType | null };
  legalBorderRef: { current: L.Polygon | null };
  buildableBorderRef: { current: L.Polygon | null };
  analysisAreaRef: { current: L.Polygon | null };
  setIsDrawHintVisible: Dispatch<SetStateAction<boolean>>;
  setDrawHintText: Dispatch<SetStateAction<string>>;
  setShowEditableCoordinatesArea: Dispatch<SetStateAction<boolean>>;
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
  setEditingPolygonType: Dispatch<SetStateAction<BorderType | null>>;
  setIsGridAnalysisAreaValid: Dispatch<SetStateAction<boolean>>;
  setGridAnalysisAreaErrorMessage: Dispatch<SetStateAction<string>>;
  cancelEditForBorder: (borderType: BorderType) => void;
  interruptBuildableOffsetMode: () => void;
  interruptLegalBorderParcelMode: () => void;
  validateLegalBuildableContainment: (borderType: BorderType | null, polygon: TurfPolygon | null) => boolean;
  validateProjectRadiusRequirement: (borderType: BorderType | null, polygon: TurfPolygon | null) => boolean;
  validateAnalysisAreaSeparation: (
    borderType: BorderType | null,
    polygon: TurfPolygon | null,
    options: { ignoreIndex?: number }
  ) => boolean;
  syncAnalysisAreas: (
    areas: Lv95Coordinate[][],
    options: {
      activeIndex?: number;
      analysisAreaType?: string;
      legalBorders?: Lv95Coordinate[][];
      buildableBorders?: Lv95Coordinate[][];
      recenter?: boolean;
    }
  ) => void;
  replaceActiveLegalBorder: (coordinates: Lv95Coordinate[]) => Lv95Coordinate[][];
  replaceActiveBuildableBorder: (coordinates: Lv95Coordinate[]) => Lv95Coordinate[][];
  addCenterMarker: AddCenterMarker;
  setGridParams: SetGridParams;
  style: typeof import('../styles/grid.tailwind').default;
  notifyError: (message: string) => void;
}

const useCadastralDrawing = ({
  mapType,
  activeEditBorderType,
  isLegalBorderParcelMode,
  setIsLegalBorderParcelMode,
  gridParams,
  analysisAreas,
  activeAnalysisAreaIndex,
  isDrawingRef,
  pendingDrawRef,
  currentTypeOfPolygonRef,
  currentShapeTypeRef,
  activeEditBorderRef,
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  setIsDrawHintVisible,
  setDrawHintText,
  setShowEditableCoordinatesArea,
  setCoordinates,
  setEditingPolygonType,
  setIsGridAnalysisAreaValid,
  setGridAnalysisAreaErrorMessage,
  cancelEditForBorder,
  interruptBuildableOffsetMode,
  interruptLegalBorderParcelMode,
  validateLegalBuildableContainment,
  validateProjectRadiusRequirement,
  validateAnalysisAreaSeparation,
  syncAnalysisAreas,
  replaceActiveLegalBorder,
  replaceActiveBuildableBorder,
  addCenterMarker,
  setGridParams,
  style,
  notifyError,
}: CadastralDrawingOptions) => {
  const startDrawing = (shapeType: ShapeType | null, borderType: BorderType | null = null) => {
    interruptBuildableOffsetMode();
    if (activeEditBorderRef.current) {
      pendingDrawRef.current = { shapeType, borderType };
      cancelEditForBorder(activeEditBorderRef.current);
      return;
    }
    highlightActiveDrawButton(shapeType, borderType);
    currentTypeOfPolygonRef.current = borderType;
    currentShapeTypeRef.current = shapeType;
    triggerDrawing(shapeType, borderType);
  };

  const handleDrawRequest = (shapeType: ShapeType, borderType: BorderType) => {
    interruptBuildableOffsetMode();
    interruptLegalBorderParcelMode();
    if (isDrawButtonActive(shapeType, borderType)) {
      setIsDrawHintVisible(false);
      setDrawHintText('');
      cancelActiveDrawing({ isDrawingRef, pendingDrawRef, currentTypeOfPolygonRef, currentShapeTypeRef });
      return;
    }
    setDrawHintText(shapeType === 'rectangle' ? RECTANGLE_DRAW_HINT : POLYGON_DRAW_HINT);
    setIsDrawHintVisible(true);
    startDrawing(shapeType, borderType);
  };

  useEffect(() => {
    if (activeEditBorderType || !pendingDrawRef.current) return;
    const { shapeType, borderType } = pendingDrawRef.current;
    pendingDrawRef.current = null;
    setTimeout(() => {
      setDrawHintText(shapeType === 'rectangle' ? RECTANGLE_DRAW_HINT : POLYGON_DRAW_HINT);
      setIsDrawHintVisible(true);
      highlightActiveDrawButton(shapeType, borderType);
      currentTypeOfPolygonRef.current = borderType;
      currentShapeTypeRef.current = shapeType;
      triggerDrawing(shapeType, borderType);
    }, 0);
  }, [activeEditBorderType]);

  useEffect(() => {
    if (activeEditBorderType && isLegalBorderParcelMode) {
      setIsLegalBorderParcelMode(false);
      setIsDrawHintVisible(false);
      setDrawHintText('');
    }
  }, [activeEditBorderType, isLegalBorderParcelMode]);

  useEffect(() => {
    if (mapType !== 'osm' || !isLegalBorderParcelMode) return;
    setIsLegalBorderParcelMode(false);
    setIsDrawHintVisible(false);
    setDrawHintText('');
  }, [isLegalBorderParcelMode, mapType]);

  const handleCreated = (event: DrawCreatedEvent) => {
    isDrawingRef.current = false;
    setIsDrawHintVisible(false);
    setDrawHintText('');
    clearActiveDrawButton();

    let layer = event.layer;
    if (event.layerType === 'rectangle' && mapType === 'cadastral') layer = normalizeRectangle(layer);
    const rawLatLngs = layer?._latlngs;
    const ring = (Array.isArray(rawLatLngs?.[0]) ? rawLatLngs[0] : rawLatLngs) as L.LatLng[];
    if (!Array.isArray(ring) || ring.length < 3) {
      layer?.remove?.();
      setTimeout(() => startDrawing(currentShapeTypeRef.current, currentTypeOfPolygonRef.current), 0);
      return;
    }

    const newCoordinates = roundCoordinatesTo8Digits(ring);
    const polygonCoordinates = newCoordinates.map((coordinate) => [coordinate.lng, coordinate.lat]);
    polygonCoordinates.push(polygonCoordinates[0]);
    const polygon = turf.polygon([polygonCoordinates]);
    const polygonArea = turf.area(polygon);
    const borderType = currentTypeOfPolygonRef.current;
    if (polygonArea < MIN_ANALYSIS_AREA) {
      layer.remove();
      setTimeout(() => startDrawing(currentShapeTypeRef.current, currentTypeOfPolygonRef.current), 0);
      return;
    }
    if (polygonArea > MAX_ALLOWED_GRID_AREA) {
      notifyError("The polygon area is greater than 10'000'000 m²!");
      layer.remove();
      return;
    }
    if (turf.kinks(polygon).features.length !== 0) {
      notifyError('The polygon intersects itself!');
      layer.remove();
      return;
    }
    if (
      !validateLegalBuildableContainment(borderType, polygon) ||
      !validateProjectRadiusRequirement(borderType, polygon) ||
      !validateAnalysisAreaSeparation(borderType, polygon, { ignoreIndex: activeAnalysisAreaIndex })
    ) {
      layer.remove();
      return;
    }

    const lv95Coordinates = newCoordinates.map((coordinate) =>
      wgs84ToLv95ForOneCoordinate(coordinate.lng, coordinate.lat)
    );
    if (!isWithinSwitzerland(lv95Coordinates, gridParams.analysisAreaType)) {
      notifyError('Every polygon point must be inside Switzerland.');
      layer.remove();
      return;
    }

    switch (borderType) {
      case 'legalBorder':
        layer.setStyle(style.legalBorder);
        syncAnalysisAreas(analysisAreas, {
          activeIndex: activeAnalysisAreaIndex,
          legalBorders: replaceActiveLegalBorder(lv95Coordinates),
        });
        legalBorderRef.current = layer;
        setCoordinates(lv95Coordinates);
        setEditingPolygonType('legalBorder');
        break;
      case 'buildableBorder':
        layer.setStyle(style.buildableBorder);
        syncAnalysisAreas(analysisAreas, {
          activeIndex: activeAnalysisAreaIndex,
          buildableBorders: replaceActiveBuildableBorder(lv95Coordinates),
        });
        buildableBorderRef.current = layer;
        setCoordinates(lv95Coordinates);
        setEditingPolygonType('buildableBorder');
        break;
      case 'analysisArea': {
        layer.setStyle(style.analysisArea);
        const nextAreas =
          analysisAreas.length === 0
            ? [lv95Coordinates]
            : analysisAreas.map((area, index) => (index === activeAnalysisAreaIndex ? lv95Coordinates : area));
        syncAnalysisAreas(nextAreas, {
          activeIndex: analysisAreas.length === 0 ? 0 : activeAnalysisAreaIndex,
          analysisAreaType: 'newAnalysisArea',
          recenter: true,
        });
        setIsGridAnalysisAreaValid(true);
        setGridAnalysisAreaErrorMessage('');
        analysisAreaRef.current = layer;
        setCoordinates(lv95Coordinates);
        setEditingPolygonType('analysisArea');
        const combinedCenter = getCombinedAnalysisAreasCenter(nextAreas);
        addCenterMarker(
          polygon,
          getLayerMap(layer),
          setGridParams,
          combinedCenter ? [combinedCenter.x, combinedCenter.y] : null
        );
        break;
      }
      default:
        break;
    }
    setShowEditableCoordinatesArea(true);
  };

  const handleDrawStart = () => {
    isDrawingRef.current = true;
    setDrawHintText(currentShapeTypeRef.current === 'rectangle' ? RECTANGLE_DRAW_HINT : POLYGON_DRAW_HINT);
    setIsDrawHintVisible(true);
    highlightActiveDrawButton(currentShapeTypeRef.current, currentTypeOfPolygonRef.current);
  };

  const handleDrawStop = () => {
    isDrawingRef.current = false;
    setIsDrawHintVisible(false);
    setDrawHintText('');
    clearActiveDrawButton();
  };

  return {
    startDrawing,
    handleDrawRequest,
    onCreated: useStableHandler(handleCreated),
    onDrawStart: useStableHandler(handleDrawStart),
    onDrawStop: useStableHandler(handleDrawStop),
  };
};

export default useCadastralDrawing;
